import { useMemo } from 'react'
import { usePinduoduoAfterSalesFacts, usePinduoduoOrderFacts } from './platform'
import {
  emptyPddAfterSalesMetrics,
  pddSecondReturnCohortByProduct,
  pddReferenceCohortIssue,
  sumPddAfterSalesMetrics,
  type PddAfterSalesMetrics,
} from './pinduoduoAfterSales'
import {
  pinduoduoStatisticsWindow,
  type PinduoduoCustomRange,
  type PinduoduoStatisticsPeriod,
} from './pinduoduoStatistics'

const addToGroup = (
  grouped: Map<string, PddAfterSalesMetrics[]>,
  key: string,
  metrics: PddAfterSalesMetrics,
) => {
  const rows = grouped.get(key)
  if (rows) rows.push(metrics)
  else grouped.set(key, [metrics])
}

export function usePinduoduoAfterSalesMetrics(
  statisticsPeriod: PinduoduoStatisticsPeriod,
  customRange: PinduoduoCustomRange,
) {
  const facts = usePinduoduoAfterSalesFacts()
  const orders = usePinduoduoOrderFacts()
  const window = useMemo(
    // Use the same requested dates as the paid-order denominator.
    // An after-sales export start is an application date, not a payment-date boundary.
    () => pinduoduoStatisticsWindow(orders.data, statisticsPeriod, customRange),
    [customRange.endDate, customRange.startDate, orders.data, statisticsPeriod],
  )
  const startDate = window?.startDate || ''
  const endDate = window?.endDate || ''
  const daily = useMemo(
    () => (facts.data?.daily || []).filter((row) => row.date >= startDate && row.date <= endDate),
    [endDate, facts.data, startDate],
  )
  const dailyProducts = useMemo(
    () => (facts.data?.dailyProducts || []).filter((row) => row.date >= startDate && row.date <= endDate),
    [endDate, facts.data, startDate],
  )
  const dailySkus = useMemo(
    () => (facts.data?.dailySkus || []).filter((row) => row.date >= startDate && row.date <= endDate),
    [endDate, facts.data, startDate],
  )
  const byProductId = useMemo(() => {
    const grouped = new Map<string, PddAfterSalesMetrics[]>()
    for (const row of dailyProducts) addToGroup(grouped, row.productId, row)
    return new Map([...grouped].map(([key, rows]) => [key, sumPddAfterSalesMetrics(rows)]))
  }, [dailyProducts])
  const byVariantId = useMemo(() => {
    const grouped = new Map<string, PddAfterSalesMetrics[]>()
    for (const row of dailySkus) if (row.variantId) addToGroup(grouped, row.variantId, row)
    return new Map([...grouped].map(([key, rows]) => [key, sumPddAfterSalesMetrics(rows)]))
  }, [dailySkus])
  const applicationCoverageByProductId = useMemo(() => {
    const result = new Map<string, { mapped: number; unmapped: number }>()
    for (const row of dailySkus) {
      const coverage = result.get(row.productId) || { mapped: 0, unmapped: 0 }
      if (row.variantId) coverage.mapped += row.applicationCount
      else coverage.unmapped += row.applicationCount
      result.set(row.productId, coverage)
    }
    return result
  }, [dailySkus])
  const mappedApplicationCount = dailySkus.reduce(
    (sum, row) => sum + (row.variantId ? row.applicationCount : 0),
    0,
  )
  const unmappedApplicationCount = dailySkus.reduce(
    (sum, row) => sum + (row.variantId ? 0 : row.applicationCount),
    0,
  )
  const paymentCohortIssue = pddReferenceCohortIssue(facts.data?.meta.paymentCohort, orders.data?.meta, startDate, endDate)
  const paymentCohortReady = paymentCohortIssue === null
  const secondReturnCohortByProductId = useMemo(() => {
    if (!paymentCohortReady) return new Map<string, { secondReturnOrderCount: number; secondReturnItemCount: number }>()
    return pddSecondReturnCohortByProduct(facts.data?.paymentCohortProducts || [], startDate, endDate)
  }, [endDate, facts.data, paymentCohortReady, startDate])
  return {
    overall: daily.length ? sumPddAfterSalesMetrics(daily) : emptyPddAfterSalesMetrics(),
    daily,
    dailyProducts,
    dailySkus,
    byProductId,
    byVariantId,
    applicationCoverageByProductId,
    mappedApplicationCount,
    unmappedApplicationCount,
    paymentCohortReady,
    paymentCohortIssue,
    paymentCohortReferenceNote: `二退率为参考值：已关联支付订单的二退件数 ÷ 已知下单件数；忽略 ${facts.data?.meta.paymentCohort?.unmatchedSecondReturnOrders ?? 0} 笔缺原单二退，不影响售后申请统计。`,
    paymentCohortObservationEnd: facts.data?.meta.paymentCohort?.observationEnd || '',
    secondReturnCohortByProductId,
    window,
    loading: facts.loading || orders.loading,
    error: facts.error || orders.error,
  }
}
